import clsx from "clsx"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { formatAmount } from "@/helpers/statFormat"
import { generateUuid } from "@/helpers/uuid"
import {
  allocationErrorKey,
  applyDelta,
  buildAttributeRows,
  draftToAllocations,
  draftTotal,
  IAllocationDraft,
  remainingBank
} from "@/modals/constructor/hero/HeroModal/helpers"
import { useAllocateAttributePointsMutation } from "@/store/apis/heroAPI"
import styles from "./AttributeList.module.scss"
import { IAttributeListProps } from "./types"

export const AttributeList: FC<IAttributeListProps> = ({
  attributes,
  bank
}) => {
  const { t } = useTranslation("hero")
  const [allocate, { isLoading }] = useAllocateAttributePointsMutation()
  const [draft, setDraft] = useState<IAllocationDraft>({})
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const rows = buildAttributeRows(attributes, draft)
  const staged = draftTotal(draft)
  const remaining = remainingBank(bank, draft)
  const isStale = remaining < 0

  const step = (code: string, delta: number, available: boolean) => {
    setErrorKey(null)
    setDraft((current) =>
      applyDelta(current, code, delta, bank, {
        enabled: available,
        ceiling: Infinity
      })
    )
  }

  const reset = () => {
    setErrorKey(null)
    setDraft({})
  }

  const confirm = async () => {
    setErrorKey(null)
    const freshIdempotencyKey = generateUuid()
    try {
      await allocate({
        requestId: freshIdempotencyKey,
        allocations: draftToAllocations(draft)
      }).unwrap()
      setDraft({})
    } catch (error) {
      setErrorKey(allocationErrorKey(error, "attributes"))
    }
  }

  return (
    <section className={styles.AttributeList}>
      <h3 className={styles.AttributeList_title}>{t("attributes.title")}</h3>
      <div className={styles.AttributeList_bank}>
        <span>{t("banks.attributePoints")}</span>
        <span>
          {bank}
          {staged > 0 && ` → ${bank - staged}`}
        </span>
      </div>
      <ul className={styles.AttributeList_rows}>
        {rows.map((row) => (
          <li
            key={row.code}
            className={clsx(styles.AttributeList_row, {
              [styles.muted]: !row.available
            })}
          >
            <span>
              {t(`catalog:stat.${row.code}`, { defaultValue: row.code })}
            </span>
            <span className={styles.AttributeList_value}>
              {row.available ? formatAmount(row.base) : t("allocate.locked")}
              {row.staged > 0 && (
                <span className={styles.AttributeList_staged}>
                  {" "}
                  +{row.staged}
                </span>
              )}
              {row.gearBonus !== 0 && (
                <span className={styles.AttributeList_gear}>
                  {" "}
                  ({row.gearBonus > 0 ? "+" : ""}
                  {formatAmount(row.gearBonus)})
                </span>
              )}
            </span>
            {row.available && (
              <span className={styles.AttributeList_controls}>
                <button
                  type="button"
                  aria-label={t("allocate.decrease", {
                    name: t(`catalog:stat.${row.code}`, {
                      defaultValue: row.code
                    })
                  })}
                  disabled={isLoading || row.staged === 0}
                  onClick={() => step(row.code, -1, row.available)}
                >
                  −
                </button>
                <button
                  type="button"
                  aria-label={t("allocate.increase", {
                    name: t(`catalog:stat.${row.code}`, {
                      defaultValue: row.code
                    })
                  })}
                  disabled={isLoading || remaining < 1}
                  onClick={() => step(row.code, 1, row.available)}
                >
                  +
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className={styles.AttributeList_actions}>
        <button
          type="button"
          disabled={isLoading || staged === 0}
          onClick={reset}
        >
          {t("allocate.reset")}
        </button>
        <button
          type="button"
          disabled={isLoading || staged === 0 || isStale}
          onClick={() => void confirm()}
        >
          {t("allocate.confirm")}
        </button>
      </div>
      {isStale && (
        <span className={styles.AttributeList_error}>
          {t("allocate.error.staleBank")}
        </span>
      )}
      {errorKey && (
        <span className={styles.AttributeList_error}>{t(errorKey)}</span>
      )}
    </section>
  )
}
